import React from 'react';
import BaseWidget from './BaseWidget';
import useModuleAccess from '../../hooks/useModuleAccess';

/**
 * QuickActionsWidget Component
 *
 * Provides quick access buttons for common actions
 */
const QuickActionsWidget = ({ id, config = {}, onRemove, onConfigure, isEditing, onAction, theme = 'light', isUser = false }) => {
  const { allowed: canEditFinancial } = useModuleAccess(isUser ? 'financial' : null, 'edit');
  const { allowed: canEditCrm } = useModuleAccess(isUser ? 'crm' : null, 'edit');
  const { allowed: canEditDispatch } = useModuleAccess(isUser ? 'dispatch' : null, 'edit');
  const { allowed: canViewReports } = useModuleAccess(isUser ? 'reports' : null, 'view');
  const { allowed: canViewSettings } = useModuleAccess(isUser ? 'settings' : null, 'view');
  const {
    actions = ['new_invoice', 'new_client', 'new_booking', 'new_expense']
  } = config;

  // Action definitions
  const actionDefinitions = {
    new_invoice: {
      label: 'New Invoice',
      icon: '📄',
      color: 'blue',
      action: 'create_invoice'
    },
    new_client: {
      label: 'New Client',
      icon: '👤',
      color: 'green',
      action: 'create_client'
    },
    new_booking: {
      label: 'New Booking',
      icon: '📅',
      color: 'purple',
      action: 'create_booking'
    },
    new_expense: {
      label: 'New Expense',
      icon: '💳',
      color: 'orange',
      action: 'create_expense'
    },
    view_reports: {
      label: 'View Reports',
      icon: '📊',
      color: 'indigo',
      action: 'view_reports'
    },
    settings: {
      label: 'Settings',
      icon: '⚙️',
      color: 'gray',
      action: 'open_settings'
    }
  };

  // Color classes for buttons
  const colorClasses = {
    blue: 'bg-blue-500 hover:bg-blue-600 text-white',
    green: 'bg-green-500 hover:bg-green-600 text-white',
    purple: 'bg-purple-500 hover:bg-purple-600 text-white',
    orange: 'bg-[var(--viz-orange)] hover:brightness-90 text-white',
    indigo: 'bg-indigo-500 hover:bg-indigo-600 text-white',
    gray: 'bg-gray-500 hover:bg-gray-600 text-white'
  };
  const actionAccess = isUser ? {
    new_invoice: canEditFinancial,
    new_expense: canEditFinancial,
    new_client: canEditCrm,
    new_booking: canEditDispatch,
    view_reports: canViewReports,
    settings: canViewSettings,
  } : {};

  const handleActionClick = (actionKey) => {
    const actionDef = actionDefinitions[actionKey];
    if (onAction && actionDef) {
      onAction(actionDef.action);
    }
  };

  return (
    <BaseWidget
      id={id}
      title="Quick Actions"
      onRemove={onRemove}
      onConfigure={onConfigure}
      isRemovable={isEditing}
      isConfigurable={isEditing}
      theme={theme}
      icon={
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      }
      headerActions={
        isEditing ? (
          <div className="widget-drag-handle px-2 py-1 cursor-move">
            <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </div>
        ) : null
      }
    >
      <div className="grid grid-cols-2 gap-3 h-full items-center">
        {actions.filter((actionKey) => actionAccess[actionKey] !== false).map(actionKey => {
          const actionDef = actionDefinitions[actionKey];
          if (!actionDef) return null;

          return (
            <button
              key={actionKey}
              onClick={() => handleActionClick(actionKey)}
              disabled={isEditing}
              className={`
                flex flex-col items-center justify-center p-4 rounded-lg
                transition-all duration-200 transform
                ${isEditing ? 'opacity-50 cursor-not-allowed' : 'hover:scale-105 shadow-sm hover:shadow-md'}
                ${colorClasses[actionDef.color] || colorClasses.blue}
              `}
            >
              <span className="text-2xl mb-2">{actionDef.icon}</span>
              <span className="text-xs font-medium text-center leading-tight">
                {actionDef.label}
              </span>
            </button>
          );
        })}
      </div>
    </BaseWidget>
  );
};

export default QuickActionsWidget;
